import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  BookOpen, ArrowLeft, Calendar, Clock, FileText, Upload, Download,
  Plus, Send, Star, CheckCircle, AlertCircle, Trash2, ChevronDown, ChevronUp,
  Paperclip, User, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const classNames = {
  nursery: "Nursery", kg1: "KG1", kg2: "KG2",
  "class-1": "Class 1", "class-2": "Class 2", "class-3": "Class 3",
  "class-4": "Class 4", "class-5": "Class 5", "class-6": "Class 6",
  jhs1: "JHS1", jhs2: "JHS2", jhs3: "JHS3"
};

const classLevels = {
  nursery: "Kindergarten", kg1: "Kindergarten", kg2: "Kindergarten",
  "class-1": "Primary", "class-2": "Primary", "class-3": "Primary",
  "class-4": "Primary", "class-5": "Primary", "class-6": "Primary",
  jhs1: "JHS", jhs2: "JHS", jhs3: "JHS"
};

const levelColors = {
  Kindergarten: { bg: "from-pink-500 to-rose-400", accent: "pink" },
  Primary: { bg: "from-blue-500 to-cyan-400", accent: "blue" },
  JHS: { bg: "from-[#002147] to-[#003366]", accent: "indigo" }
};

const getToken = () => localStorage.getItem("token");

const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
  "Content-Type": "application/json"
});

// ---- Create Assignment Form (Teacher) ----
const CreateAssignmentForm = ({ classId, onCreated, onCancel }) => {
  const [form, setForm] = useState({ title: "", description: "", subject: "", due_date: "", max_points: 100 });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (e) => {
    setFiles(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const uploadFiles = async () => {
    const fileIds = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "assignment");
      const res = await fetch(`${API}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        fileIds.push(data.id);
      }
    }
    return fileIds;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let attachments = [];
      if (files.length > 0) attachments = await uploadFiles();
      const res = await fetch(`${API}/api/assignments`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ ...form, class_id: classId, attachments })
      });
      if (res.ok) onCreated();
    } catch (err) {
      console.error("Failed to create assignment:", err);
    }
    setUploading(false);
  };

  return (
    <Card className="border-2 border-orange-200 bg-orange-50/50" data-testid="create-assignment-form">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-[#002147] flex items-center gap-2">
          <Plus size={18} /> Post New Assignment
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text" required value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-400 text-sm"
                placeholder="e.g. Week 3 Mathematics Homework"
                data-testid="assignment-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
              <input
                type="text" required value={form.subject}
                onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-400 text-sm"
                placeholder="e.g. Mathematics"
                data-testid="assignment-subject-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              required rows={3} value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-400 text-sm"
              placeholder="Describe the assignment..."
              data-testid="assignment-desc-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
              <input
                type="date" required value={form.due_date}
                onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-400 text-sm"
                data-testid="assignment-due-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Points</label>
              <input
                type="number" min={1} max={1000} value={form.max_points}
                onChange={e => setForm(p => ({ ...p, max_points: parseInt(e.target.value) || 100 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-400 text-sm"
                data-testid="assignment-points-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {files.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-white border px-2 py-1 rounded text-xs">
                  <Paperclip size={12} /> {f.name}
                  <button type="button" onClick={() => removeFile(i)} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                </span>
              ))}
            </div>
            <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-orange-300 hover:text-orange-500 transition-colors">
              <Upload size={14} /> Add Files
              <input type="file" multiple className="hidden" onChange={handleFileSelect} data-testid="assignment-file-input" />
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={uploading} className="bg-[#FF6B00] hover:bg-orange-600 text-white" data-testid="post-assignment-btn">
              {uploading ? "Posting..." : "Post Assignment"}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-assignment-btn">Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ---- Student Submission Form ----
const SubmitWork = ({ assignment, existingSubmission, onSubmitted }) => {
  const [content, setContent] = useState(existingSubmission?.content || "");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let fileIds = existingSubmission?.file_ids || [];
      if (files.length > 0) {
        for (const file of files) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("category", "submission");
          const res = await fetch(`${API}/api/upload`, {
            method: "POST",
            headers: { Authorization: `Bearer ${getToken()}` },
            body: formData
          });
          if (res.ok) {
            const data = await res.json();
            fileIds.push(data.id);
          }
        }
      }
      const res = await fetch(`${API}/api/submissions`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ assignment_id: assignment.id, content, file_ids: fileIds })
      });
      if (res.ok) onSubmitted();
    } catch (err) {
      console.error("Submit failed:", err);
    }
    setUploading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 mt-3 p-4 bg-green-50 rounded-lg border border-green-200" data-testid="submit-work-form">
      <h4 className="text-sm font-semibold text-green-800 flex items-center gap-1">
        <Upload size={14} /> {existingSubmission ? "Update Your Submission" : "Submit Your Work"}
      </h4>
      <textarea
        rows={2} value={content}
        onChange={e => setContent(e.target.value)}
        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-300"
        placeholder="Add notes about your submission (optional)..."
        data-testid="submission-content-input"
      />
      <div className="flex flex-wrap items-center gap-3">
        <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 border border-dashed rounded text-xs text-gray-500 hover:border-green-400 hover:text-green-600">
          <Paperclip size={12} /> Upload Files
          <input type="file" multiple className="hidden" onChange={e => setFiles(Array.from(e.target.files))} data-testid="submission-file-input" />
        </label>
        {files.map((f, i) => (
          <span key={i} className="text-xs bg-white border px-2 py-1 rounded flex items-center gap-1">
            <FileText size={10} /> {f.name}
            <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-red-400"><X size={10} /></button>
          </span>
        ))}
      </div>
      <Button type="submit" disabled={uploading} size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-work-btn">
        <Send size={14} className="mr-1" /> {uploading ? "Uploading..." : existingSubmission ? "Re-submit" : "Submit"}
      </Button>
    </form>
  );
};

// ---- Grade Form (Teacher) ----
const GradeForm = ({ submission, maxPoints, onGraded }) => {
  const [grade, setGrade] = useState(submission.grade ?? "");
  const [feedback, setFeedback] = useState(submission.feedback || "");
  const [saving, setSaving] = useState(false);

  const handleGrade = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/submissions/${submission.id}/grade`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ grade: parseInt(grade), feedback })
      });
      if (res.ok) onGraded();
    } catch (err) {
      console.error("Grade failed:", err);
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleGrade} className="flex flex-wrap items-end gap-2 mt-2" data-testid={`grade-form-${submission.id}`}>
      <div>
        <label className="text-xs text-gray-500">Grade (/{maxPoints})</label>
        <input
          type="number" min={0} max={maxPoints} required value={grade}
          onChange={e => setGrade(e.target.value)}
          className="w-20 px-2 py-1 border rounded text-sm"
          data-testid={`grade-input-${submission.id}`}
        />
      </div>
      <div className="flex-1 min-w-[150px]">
        <label className="text-xs text-gray-500">Feedback</label>
        <input
          type="text" value={feedback}
          onChange={e => setFeedback(e.target.value)}
          className="w-full px-2 py-1 border rounded text-sm"
          placeholder="Optional feedback..."
          data-testid={`feedback-input-${submission.id}`}
        />
      </div>
      <Button type="submit" size="sm" disabled={saving} className="bg-[#002147] hover:bg-[#003366] text-white" data-testid={`save-grade-btn-${submission.id}`}>
        <Star size={12} className="mr-1" /> {saving ? "..." : "Save Grade"}
      </Button>
    </form>
  );
};

// ---- Assignment Card ----
const AssignmentCard = ({ assignment, userType, submissions, mySubmission, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isPastDue = new Date(assignment.due_date) < new Date();
  const assignmentSubmissions = submissions.filter(s => s.assignment_id === assignment.id);

  const handleDelete = async () => {
    if (!window.confirm("Delete this assignment and all submissions?")) return;
    setDeleting(true);
    try {
      await fetch(`${API}/api/assignments/${assignment.id}`, {
        method: "DELETE",
        headers: authHeaders()
      });
      onRefresh();
    } catch (err) { console.error(err); }
    setDeleting(false);
  };

  const downloadFile = (fileId) => {
    window.open(`${API}/api/files/${fileId}`, "_blank");
  };

  return (
    <Card className="border hover:shadow-md transition-shadow" data-testid={`assignment-card-${assignment.id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="font-semibold text-[#002147]">{assignment.title}</h3>
              <Badge variant="outline" className="text-xs">{assignment.subject}</Badge>
              {isPastDue && <Badge className="bg-red-100 text-red-700 text-xs">Past Due</Badge>}
              {mySubmission && (
                <Badge className={mySubmission.status === "graded" ? "bg-green-100 text-green-700 text-xs" : "bg-blue-100 text-blue-700 text-xs"}>
                  {mySubmission.status === "graded" ? `Graded: ${mySubmission.grade}/${assignment.max_points}` : "Submitted"}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><User size={12} /> {assignment.teacher_name}</span>
              <span className="flex items-center gap-1"><Calendar size={12} /> Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
              <span className="flex items-center gap-1"><Star size={12} /> {assignment.max_points} pts</span>
              {userType !== "student" && <span className="flex items-center gap-1"><FileText size={12} /> {assignmentSubmissions.length} submissions</span>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {userType !== "student" && (
              <button onClick={handleDelete} disabled={deleting} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors" data-testid={`delete-assignment-${assignment.id}`}>
                <Trash2 size={16} />
              </button>
            )}
            <button onClick={() => setExpanded(!expanded)} className="p-1.5 text-gray-400 hover:text-[#002147] transition-colors" data-testid={`toggle-assignment-${assignment.id}`}>
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">{assignment.description}</div>

            {/* Attachment downloads */}
            {assignment.attachments?.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500">Attachments:</p>
                <div className="flex flex-wrap gap-2">
                  {assignment.attachments.map((fileId, i) => (
                    <button key={i} onClick={() => downloadFile(fileId)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs hover:bg-blue-100 transition-colors"
                      data-testid={`download-attachment-${i}`}>
                      <Download size={12} /> Attachment {i + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Student: Submit Work */}
            {userType === "student" && (
              <>
                {mySubmission && !showSubmitForm && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-800 flex items-center gap-1">
                          <CheckCircle size={14} /> Submitted on {new Date(mySubmission.submitted_at).toLocaleDateString()}
                          {mySubmission.status === "resubmitted" && <Badge className="bg-yellow-100 text-yellow-700 text-xs ml-1">Resubmitted</Badge>}
                        </p>
                        {mySubmission.content && <p className="text-xs text-blue-600 mt-1">{mySubmission.content}</p>}
                        {mySubmission.file_ids?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {mySubmission.file_ids.map((fid, i) => (
                              <button key={i} onClick={() => downloadFile(fid)}
                                className="text-xs bg-white px-2 py-1 rounded border text-blue-600 hover:bg-blue-50 flex items-center gap-1">
                                <Download size={10} /> File {i + 1}
                              </button>
                            ))}
                          </div>
                        )}
                        {mySubmission.status === "graded" && (
                          <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-sm font-bold text-green-800">Grade: {mySubmission.grade}/{assignment.max_points}</p>
                            {mySubmission.feedback && <p className="text-xs text-green-700 mt-1">{mySubmission.feedback}</p>}
                          </div>
                        )}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setShowSubmitForm(true)} data-testid={`resubmit-btn-${assignment.id}`}>
                        <Upload size={12} className="mr-1" /> Re-upload
                      </Button>
                    </div>
                  </div>
                )}
                {(showSubmitForm || !mySubmission) && (
                  <SubmitWork
                    assignment={assignment}
                    existingSubmission={mySubmission}
                    onSubmitted={() => { setShowSubmitForm(false); onRefresh(); }}
                  />
                )}
              </>
            )}

            {/* Teacher: View Submissions */}
            {userType !== "student" && (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-[#002147] flex items-center gap-1">
                  <FileText size={14} /> Student Submissions ({assignmentSubmissions.length})
                </h4>
                {assignmentSubmissions.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No submissions yet</p>
                ) : (
                  assignmentSubmissions.map(sub => (
                    <div key={sub.id} className="p-3 bg-gray-50 rounded-lg border" data-testid={`submission-${sub.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium text-[#002147]">{sub.student_name}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(sub.submitted_at).toLocaleString()}
                            {sub.status === "resubmitted" && <Badge className="bg-yellow-100 text-yellow-700 text-xs ml-1">Resubmitted</Badge>}
                          </p>
                        </div>
                        {sub.status === "graded" && (
                          <Badge className="bg-green-100 text-green-700">{sub.grade}/{assignment.max_points}</Badge>
                        )}
                      </div>
                      {sub.content && <p className="text-sm text-gray-600 mb-2">{sub.content}</p>}
                      {sub.file_ids?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {sub.file_ids.map((fid, i) => (
                            <button key={i} onClick={() => downloadFile(fid)}
                              className="text-xs bg-white px-2 py-1 rounded border text-blue-600 hover:bg-blue-50 flex items-center gap-1"
                              data-testid={`download-sub-file-${sub.id}-${i}`}>
                              <Download size={10} /> Download File {i + 1}
                            </button>
                          ))}
                        </div>
                      )}
                      <GradeForm submission={sub} maxPoints={assignment.max_points} onGraded={onRefresh} />
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

// ---- Main Page ----
const ClassAssignmentsPage = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const className = classNames[classId] || classId;
  const level = classLevels[classId] || "Primary";
  const colors = levelColors[level] || levelColors.Primary;

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const [aRes, sRes] = await Promise.all([
        fetch(`${API}/api/assignments?class_id=${classId}`, { headers: authHeaders() }),
        fetch(`${API}/api/submissions`, { headers: authHeaders() })
      ]);
      if (aRes.ok) setAssignments(await aRes.json());
      if (sRes.ok) setSubmissions(await sRes.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [classId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isTeacher = user?.user_type === "teacher" || user?.user_type === "admin";

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="class-assignments-page">
      {/* Hero */}
      <div className={`bg-gradient-to-r ${colors.bg} text-white py-10`}>
        <div className="max-w-5xl mx-auto px-4">
          <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 mb-3"
            onClick={() => navigate("/course-catalog")} data-testid="back-to-courses">
            <ArrowLeft size={18} className="mr-2" /> Back to Courses
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <BookOpen size={24} />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">{className} Assignments</h1>
                <p className="text-white/80 text-sm">{level} Level - Young Christian Preparatory School / JHS</p>
              </div>
            </div>
            {isTeacher && !showCreateForm && (
              <Button onClick={() => setShowCreateForm(true)} className="bg-white text-[#002147] hover:bg-white/90" data-testid="new-assignment-btn">
                <Plus size={16} className="mr-1" /> New Assignment
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {!user && (
          <Card className="bg-white">
            <CardContent className="p-8 text-center">
              <AlertCircle size={40} className="mx-auto text-orange-400 mb-3" />
              <h3 className="text-lg font-semibold text-[#002147] mb-2">Login Required</h3>
              <p className="text-gray-500 text-sm mb-4">Please log in as a teacher to post assignments or as a student to view and submit work.</p>
              <Button onClick={() => navigate("/")} className="bg-[#FF6B00] hover:bg-orange-600 text-white" data-testid="login-redirect-btn">
                Go to Login
              </Button>
            </CardContent>
          </Card>
        )}

        {user && showCreateForm && (
          <CreateAssignmentForm
            classId={classId}
            onCreated={() => { setShowCreateForm(false); fetchData(); }}
            onCancel={() => setShowCreateForm(false)}
          />
        )}

        {user && loading && (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-[#002147] border-t-transparent rounded-full mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading assignments...</p>
          </div>
        )}

        {user && !loading && assignments.length === 0 && (
          <Card className="bg-white">
            <CardContent className="p-8 text-center">
              <Calendar size={40} className="mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-semibold text-[#002147] mb-1">No Assignments Yet</h3>
              <p className="text-gray-500 text-sm">
                {isTeacher ? "Click \"New Assignment\" above to post the first assignment for this class." : "No assignments have been posted for this class yet. Check back later."}
              </p>
            </CardContent>
          </Card>
        )}

        {user && !loading && assignments.map(a => (
          <AssignmentCard
            key={a.id}
            assignment={a}
            userType={user.user_type}
            submissions={submissions}
            mySubmission={user.user_type === "student" ? submissions.find(s => s.assignment_id === a.id && s.student_id === user.id) : null}
            onRefresh={fetchData}
          />
        ))}
      </div>
    </div>
  );
};

export default ClassAssignmentsPage;
